import React, { useEffect, useMemo, useState } from "react";
import {
  ChevronRight,
  Heart,
  Loader2,
  LogIn,
  MapPin,
  RefreshCw,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useQueries, useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";

import Navbar from "./Navbar";
import Footer from "./Footer";
import NoImg from "../assets/images/no-image-icon-23485.png";
import starGreen from "../assets/icons/StarGreen.svg";
import useUserStore from "../store/userStore";
import { useFavorites, useRemoveFavorite } from "../hooks/useFavorites";
import type { Favorite, FavoriteTarget } from "../types/favorite";

type FavoriteFilter = "all" | FavoriteTarget;

const getFavoriteTarget = (favorite: Favorite): FavoriteTarget =>
  favorite.model_type ?? favorite.item_type ?? "place";

type PlaceDetails = {
  place_id: string;
  name: string;
  images?: Array<{ file_url: string }>;
  address?: string;
  description?: string;
  rate?: number | null;
  avg_rating?: number | null;
  review_count?: number;
};

type PlanDetails = {
  id: number;
  name: string;
  description?: string;
  steps?: Array<{
    place: string;
    place_name?: string;
    first_image_url?: string | null;
  }>;
};

type CityDetails = {
  id: number;
  name: string;
  image?: string;
  place_count?: number;
  description?: string;
};

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const fetchPlaceDetails = async (placeId: string): Promise<PlaceDetails> => {
  const response = await fetch(`${BASE_URL}/api/places/${placeId}/`);

  if (!response.ok) {
    throw new Error("Failed to fetch place details");
  }

  const body = await response.json();
  return body.data ?? body;
};

const fetchPlanDetails = async (planId: string): Promise<PlanDetails> => {
  const response = await fetch(`${BASE_URL}/api/plans/${planId}/`);

  if (!response.ok) {
    throw new Error("Failed to fetch plan details");
  }

  const body = await response.json();
  return body.data ?? body;
};

const fetchCities = async (): Promise<CityDetails[]> => {
  const response = await fetch(`${BASE_URL}/api/cities/`);
  if (!response.ok) throw new Error("Failed to fetch cities");

  const body = await response.json();
  return Array.isArray(body) ? body : body.data ?? [];
};

const getPlaceImageUrl = (place: PlaceDetails | undefined) => {
  const fileUrl = place?.images?.[0]?.file_url;

  if (!fileUrl) return NoImg;
  if (/^https?:\/\//i.test(fileUrl)) return fileUrl;

  return `${BASE_URL ?? ""}${fileUrl}`;
};

const getImageUrl = (fileUrl?: string | null) => {
  if (!fileUrl) return NoImg;
  if (/^https?:\/\//i.test(fileUrl)) return fileUrl;

  return `${BASE_URL ?? ""}${fileUrl}`;
};

const ImageWithBlurPreview: React.FC<{
  src: string;
  alt: string;
  isLoading: boolean;
}> = ({ src, alt, isLoading }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(false);
  }, [src]);

  if (isLoading) {
    return <div className="h-48 w-full animate-pulse bg-gray-200" />;
  }

  return (
    <div className="relative h-48 w-full overflow-hidden">
      <img
        src={src}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full scale-110 object-cover blur-xl brightness-90"
      />
      <img
        src={src}
        alt={alt}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        onError={(event) => {
          event.currentTarget.src = NoImg;
          setLoaded(true);
        }}
        className={`absolute inset-0 h-full w-full object-cover transition-all duration-700 group-hover:brightness-75 ${
          loaded ? "opacity-100" : "opacity-0"
        }`}
      />
    </div>
  );
};

const Favorites: React.FC = () => {
  const { user } = useUserStore();
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<FavoriteFilter>("all");
  const {
    data: favorites = [],
    isLoading,
    isError,
    refetch,
  } = useFavorites(user?.userId);
  const removeFavoriteMutation = useRemoveFavorite(user?.userId);

  const placeFavorites = useMemo(
    () => favorites.filter((favorite) => getFavoriteTarget(favorite) === "place"),
    [favorites]
  );
  const planFavorites = useMemo(
    () => favorites.filter((favorite) => getFavoriteTarget(favorite) === "plan"),
    [favorites]
  );
  const cityFavorites = useMemo(
    () => favorites.filter((favorite) => getFavoriteTarget(favorite) === "city"),
    [favorites]
  );
  const { data: cities = [], isLoading: citiesLoading } = useQuery({
    queryKey: ["cities"],
    queryFn: fetchCities,
    enabled: cityFavorites.length > 0,
    staleTime: 10 * 60 * 1000,
  });
  const cityDetails = new Map(cities.map((city) => [String(city.id), city]));
  const placeQueries = useQueries({
    queries: placeFavorites.map((favorite) => ({
      queryKey: ["place", favorite.object_id],
      queryFn: () => fetchPlaceDetails(favorite.object_id),
      enabled: Boolean(favorite.object_id),
      staleTime: 10 * 60 * 1000,
    })),
  });
  const placeImages = new Map(
    placeFavorites.map((favorite, index) => [
      favorite.object_id,
      getPlaceImageUrl(placeQueries[index]?.data),
    ])
  );
  const placeDetails = new Map(
    placeFavorites.map((favorite, index) => [
      favorite.object_id,
      placeQueries[index]?.data,
    ])
  );
  const placeImageLoading = new Map(
    placeFavorites.map((favorite, index) => [
      favorite.object_id,
      placeQueries[index]?.isLoading ?? false,
    ])
  );
  const planQueries = useQueries({
    queries: planFavorites.map((favorite) => ({
      queryKey: ["plan", favorite.object_id],
      queryFn: () => fetchPlanDetails(favorite.object_id),
      enabled: Boolean(favorite.object_id),
      staleTime: 10 * 60 * 1000,
    })),
  });
  const planFirstSteps = planQueries.map((query) => query.data?.steps?.[0]);
  const planPlaceQueries = useQueries({
    queries: planFirstSteps.map((step) => ({
      queryKey: ["place", step?.place],
      queryFn: () => fetchPlaceDetails(step!.place),
      enabled: Boolean(step?.place) && !step?.first_image_url,
      staleTime: 10 * 60 * 1000,
    })),
  });
  const planImages = new Map(
    planFavorites.map((favorite, index) => {
      const firstStep = planFirstSteps[index];
      const imageUrl = firstStep?.first_image_url
        ? getImageUrl(firstStep.first_image_url)
        : getPlaceImageUrl(planPlaceQueries[index]?.data);

      return [favorite.object_id, imageUrl];
    })
  );
  const planDetails = new Map(
    planFavorites.map((favorite, index) => [
      favorite.object_id,
      planQueries[index]?.data,
    ])
  );
  const planImageLoading = new Map(
    planFavorites.map((favorite, index) => {
      const firstStep = planFirstSteps[index];
      const isLoading =
        (planQueries[index]?.isLoading ?? false) ||
        (Boolean(firstStep?.place) &&
          !firstStep?.first_image_url &&
          (planPlaceQueries[index]?.isLoading ?? false));

      return [favorite.object_id, isLoading];
    })
  );

  const counts = useMemo(
    () => ({
      all: favorites.length,
      place: favorites.filter((favorite) => getFavoriteTarget(favorite) === "place")
        .length,
      plan: favorites.filter((favorite) => getFavoriteTarget(favorite) === "plan")
        .length,
      city: favorites.filter((favorite) => getFavoriteTarget(favorite) === "city")
        .length,
    }),
    [favorites]
  );

  const visibleFavorites = useMemo(
    () =>
      activeFilter === "all"
        ? favorites
        : favorites.filter(
            (favorite) => getFavoriteTarget(favorite) === activeFilter
          ),
    [activeFilter, favorites]
  );

  useEffect(() => {
    if (isError) {
      toast.error("دریافت علاقه‌مندی‌ها با خطا مواجه شد", {
        className: "toast",
        hideProgressBar: true,
      });
    }
  }, [isError]);

  const handleRemoveFavorite = async (favorite: Favorite) => {
    try {
      await removeFavoriteMutation.mutateAsync(favorite);
      toast.success(
        `${favorite.item_name || "مورد انتخاب‌شده"} از علاقه‌مندی‌ها حذف شد`,
        {
          className: "toast",
          hideProgressBar: true,
        }
      );
    } catch (removeError) {
      console.error("Error removing favorite:", removeError);
      toast.error("حذف از علاقه‌مندی‌ها با خطا مواجه شد", {
        className: "toast",
        hideProgressBar: true,
      });
    }
  };

  const filterOptions: Array<{ id: FavoriteFilter; label: string }> = [
    { id: "all", label: "همه" },
    { id: "place", label: "مکان‌ها" },
    { id: "city", label: "شهرها" },
    { id: "plan", label: "برنامه‌های سفر" },
  ];

  return (
    <div
      className="min-h-screen flex flex-col font-myYekanFaNumRegular"
      dir="rtl"
    >
      <Navbar />

      <main className="site-container flex-1 pb-6 pt-20 tablet:py-10">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-5 inline-flex items-center gap-1.5 text-sm text-[#646464] hover:text-[#212121] transition-colors font-myYekanFaNumMedium"
        >
          <ChevronRight size={19} aria-hidden="true" />
          بازگشت
        </button>

        <section className="relative overflow-hidden rounded-[24px] border border-[#97d2b5] bg-gradient-to-l from-[#f3fefa] via-white to-[#cffae7] px-5 py-7 tablet:px-9 tablet:py-9 shadow-[0_8px_30px_rgba(51,88,96,0.06)]">
          <div className="absolute -left-12 -top-16 h-40 w-40 rounded-full bg-[#038545]/10" />
          <div className="absolute -bottom-20 right-1/3 h-36 w-36 rounded-full bg-[#038545]/10" />

          <div className="relative flex flex-col gap-5 tablet:flex-row tablet:items-center tablet:justify-between">
            <div className="flex items-center gap-4">
              {/* <div className="flex h-14 w-14 tablet:h-16 tablet:w-16 shrink-0 items-center justify-center rounded-2xl bg-[#038545] text-white shadow-lg shadow-[#038545]/20">
                <Heart size={30} fill="currentColor" aria-hidden="true" />
              </div> */}

              <div>
                <h1 className="text-xl tablet:text-2xl text-[#212121] font-myYekanFaNumDemiBold">
                  علاقه‌مندی‌های من
                </h1>
                <p className="mt-1.5 text-sm leading-7 text-[#646464] font-myYekanFaNumRegular">
                  مکان‌ها و برنامه‌هایی که برای سفر بعدی ذخیره کرده‌اید
                </p>
              </div>
            </div>

            {user && !isLoading && !isError && (
              <div className="w-fit rounded-full border border-white/80 bg-white/80 px-4 py-2 text-sm text-[#038545] shadow-sm backdrop-blur font-myYekanFaNumDemiBold">
                {counts.all.toLocaleString("fa-IR")} مورد ذخیره‌شده
              </div>
            )}
          </div>
        </section>

        {!user && (
          <section className="mt-6 rounded-[24px] border border-[#E2E2E2] bg-white px-5 py-14 text-center shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#f3fefa] text-[#038545]">
              <LogIn size={30} aria-hidden="true" />
            </div>
            <h2 className="mt-5 text-lg text-[#212121] font-myYekanFaNumDemiBold">
              برای دیدن علاقه‌مندی‌ها وارد شوید
            </h2>
            <p className="mx-auto mt-2 max-w-md text-sm leading-7 text-[#6F6F6F]">
              با ورود به حساب کاربری، همه مکان‌ها و برنامه‌های ذخیره‌شده شما در دسترس خواهند بود.
            </p>
            <Link
              to="/login"
              className="mt-6 inline-flex items-center justify-center rounded-xl bg-[#038545] px-6 py-3 text-sm text-white transition-colors hover:bg-[#068646] font-myYekanFaNumDemiBold"
            >
              ورود به حساب کاربری
            </Link>
          </section>
        )}

        {user && isLoading && (
          <section className="mt-6" aria-label="در حال دریافت علاقه‌مندی‌ها">
            <div className="mb-5 h-12 animate-pulse rounded-2xl bg-[#ECEEEF]" />
            <div className="grid grid-cols-1 gap-5 tablet:grid-cols-2 desktop:grid-cols-3">
              {[0, 1, 2, 3, 4, 5].map((item) => (
                <div
                  key={item}
                  className="h-52 animate-pulse rounded-[20px] border border-[#ECEEEF] bg-white"
                />
              ))}
            </div>
          </section>
        )}

        {user && !isLoading && isError && (
          <section className="mt-6 rounded-[24px] border border-[#FDD9D7] bg-white px-5 py-14 text-center shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#FCE0CC] text-[#941A13]">
              <RefreshCw size={28} aria-hidden="true" />
            </div>
            <h2 className="mt-5 text-lg text-[#212121] font-myYekanFaNumDemiBold">
              دریافت اطلاعات انجام نشد
            </h2>
            <p className="mt-2 text-sm text-[#6F6F6F]">
              اتصال اینترنت را بررسی کنید و دوباره تلاش کنید.
            </p>
            <button
              type="button"
              onClick={() => void refetch()}
              className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#038545] px-6 py-3 text-sm text-white transition-colors hover:bg-[#068646] font-myYekanFaNumDemiBold"
            >
              <RefreshCw size={17} aria-hidden="true" />
              تلاش دوباره
            </button>
          </section>
        )}

        {user && !isLoading && !isError && favorites.length === 0 && (
          <section className="mt-6 rounded-[24px] border border-[#E2E2E2] bg-white px-5 py-14 text-center shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#cffae7] text-[#038545]">
              <Heart size={36} aria-hidden="true" />
            </div>
            <h2 className="mt-5 text-lg text-[#212121] font-myYekanFaNumDemiBold">
              هنوز چیزی ذخیره نکرده‌اید
            </h2>
            <p className="mx-auto mt-2 max-w-md text-sm leading-7 text-[#6F6F6F]">
              هنگام دیدن مکان‌ها، قلب کنار هر مورد را انتخاب کنید تا بعداً به‌راحتی آن را پیدا کنید.
            </p>
            <button
              type="button"
              onClick={() => navigate("/")}
              className="mt-6 rounded-xl bg-[#038545] px-6 py-3 text-sm text-white transition-colors hover:bg-[#068646] font-myYekanFaNumDemiBold"
            >
              جست‌وجوی مکان‌ها
            </button>
          </section>
        )}

        {user && !isLoading && !isError && favorites.length > 0 && (
          <section className="mt-6">
            <div className="mb-5 flex flex-col gap-3 rounded-2xl border border-[#E9ECEE] bg-white p-2.5 tablet:flex-row tablet:items-center tablet:justify-between">
              <div
                className="flex gap-2 overflow-x-auto hide-scrollbar"
                role="group"
                aria-label="فیلتر علاقه‌مندی‌ها"
              >
                {filterOptions.map((filter) => {
                  const isActive = activeFilter === filter.id;

                  return (
                    <button
                      key={filter.id}
                      type="button"
                      onClick={() => setActiveFilter(filter.id)}
                      aria-pressed={isActive}
                      className={`shrink-0 rounded-xl px-4 py-2.5 text-sm transition-colors font-myYekanFaNumMedium ${
                        isActive
                          ? "bg-[#038545] text-white shadow-sm"
                          : "text-[#646464] hover:bg-[#F5F7F8] hover:text-[#212121]"
                      }`}
                    >
                      {filter.label}
                      <span
                        className={`mr-2 rounded-full px-2 py-0.5 text-xs ${
                          isActive ? "bg-white/20" : "bg-[#F0F1F2]"
                        }`}
                      >
                        {counts[filter.id].toLocaleString("fa-IR")}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* <p className="px-2 text-xs text-[#8E8E8E]">
                برای مشاهده جزئیات، روی هر کارت بزنید
              </p> */}
            </div>

            {visibleFavorites.length === 0 ? (
              <div className="rounded-[20px] border border-dashed border-[#CFCFCF] bg-white px-5 py-12 text-center">
                <p className="text-sm text-[#646464]">
                  موردی در این دسته ذخیره نشده است.
                </p>
                <button
                  type="button"
                  onClick={() => setActiveFilter("all")}
                  className="mt-4 text-sm text-[#038545] hover:underline font-myYekanFaNumDemiBold"
                >
                  نمایش همه علاقه‌مندی‌ها
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-5 tablet:grid-cols-2 desktop:grid-cols-3">
                {visibleFavorites.map((favorite) => {
                  const isPlan = getFavoriteTarget(favorite) === "plan";
                  const isCity = getFavoriteTarget(favorite) === "city";
                  const isRemoving =
                    removeFavoriteMutation.isPending &&
                    removeFavoriteMutation.variables?.id === favorite.id;
                  const city = cityDetails.get(favorite.object_id);
                  const destination = isPlan
                    ? `/plans/${favorite.object_id}`
                    : isCity
                      ? `/places/city/${encodeURIComponent(city?.name ?? favorite.item_name ?? "")}`
                      : `/places/${favorite.object_id}`;
                  const place = placeDetails.get(favorite.object_id);
                  const plan = planDetails.get(favorite.object_id);
                  const rating = place?.avg_rating ?? place?.rate ?? 0;
                  const reviewCount = place?.review_count ?? 0;
                  const description = isPlan
                    ? plan?.description
                    : isCity
                      ? city?.description
                      : place?.description;
                  const location = isPlan
                    ? plan?.steps?.[0]?.place_name
                    : isCity
                      ? (city?.place_count ? `${city.place_count} مکان` : "")
                      : place?.address;
                  const image = isPlan
                    ? planImages.get(favorite.object_id) ?? NoImg
                    : isCity
                      ? getImageUrl(city?.image)
                      : placeImages.get(favorite.object_id) ?? NoImg;
                  const imageIsLoading = isPlan
                    ? planImageLoading.get(favorite.object_id) ?? false
                    : isCity
                      ? citiesLoading
                      : placeImageLoading.get(favorite.object_id) ?? false;

                  return (
                    <article
                      key={favorite.id}
                      className="group relative overflow-hidden rounded-lg border border-gray-200 bg-white transition hover:shadow-md"
                    >
                      <button
                        type="button"
                        onClick={() => handleRemoveFavorite(favorite)}
                        disabled={removeFavoriteMutation.isPending}
                        aria-label={`حذف ${
                          favorite.item_name || "این مورد"
                        } از علاقه‌مندی‌ها`}
                        title="حذف از علاقه‌مندی‌ها"
                        className="absolute left-2 top-2 z-20 flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-[#E5484D] shadow-sm transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {isRemoving ? (
                          <Loader2 size={18} className="animate-spin" />
                        ) : (
                          <Heart size={18} fill="currentColor" />
                        )}
                      </button>

                      <Link
                        to={destination}
                        className="block h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#038545]"
                      >
                        <div className="relative">
                          <ImageWithBlurPreview
                            src={image}
                            alt={
                              favorite.item_name ||
                              (isPlan ? "تصویر برنامه سفر" : isCity ? "تصویر شهر" : "تصویر مکان")
                            }
                            isLoading={imageIsLoading}
                          />
                          <span className="absolute right-2 top-2 rounded-md bg-white/90 px-2 py-1 text-[10px] text-gray-700 shadow-sm backdrop-blur-sm font-myYekanFaNumMedium">
                            {isPlan ? "برنامه سفر" : isCity ? "شهر" : "مکان"}
                          </span>
                        </div>

                        <div className="p-3 text-black font-myYekanFaNumRegular">
                          <div className="flex items-center justify-between gap-3">
                            <h2 className="min-w-0 flex-1 line-clamp-1 text-sm font-myYekanFaNumDemiBold">
                              {favorite.item_name || plan?.name || city?.name || place?.name || "بدون نام"}
                            </h2>

                            {isPlan ? (
                              <span className="shrink-0 text-xs text-gray-500">
                                ({plan?.steps?.length ?? 0} مقصد)
                              </span>
                            ) : !isCity && (
                              <div className="flex shrink-0 items-center gap-2">
                                {reviewCount > 0 && (
                                  <span className="text-xs text-gray-500">
                                    ({reviewCount} نظر)
                                  </span>
                                )}
                                {rating > 0 && (
                                  <span className="flex items-center gap-1 text-sm text-[#038545]">
                                    {Number(rating.toFixed(1))}
                                    <img
                                      src={starGreen}
                                      className="mb-1 h-3 w-3"
                                      alt="امتیاز"
                                    />
                                  </span>
                                )}
                              </div>
                            )}
                          </div>

                          {location && (
                            <div className="mt-2 flex items-start text-[11px] text-gray-800">
                              <MapPin
                                size={12}
                                className="ml-1 mt-0.5 shrink-0"
                                aria-hidden="true"
                              />
                              <span className="line-clamp-1">{location}</span>
                            </div>
                          )}

                          {description && (
                            <div className="mt-2 line-clamp-2 text-justify text-[10px] text-gray-500 font-myYekanFaNumRegular">
                              <ReactMarkdown>{description}</ReactMarkdown>
                            </div>
                          )}
                        </div>
                      </Link>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default Favorites;
